import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Duration } from 'date-fns'

function useTimeParser() {
  const { t } = useI18n()

  const translatedHours = computed(() => ({
    short: t('h'),
    long: t('hours')
  }))
  const translatedMinutes = computed(() => ({
    short: t('min.'),
    long: t('minutes')
  }))
  const translatedSeconds = computed(() => ({
    short: t('sec.'),
    long: t('seconds')
  }))

  function normalizeDuration(duration: Duration): Duration {
    let { days = 0, hours = 0, minutes = 0, seconds = 0 } = duration

    if (seconds >= 60) {
      minutes += Math.floor(seconds / 60)
      seconds = seconds % 60
    }

    if (minutes >= 60) {
      hours += Math.floor(minutes / 60)
      minutes = minutes % 60
    }

    if (hours >= 24) {
      days += Math.floor(hours / 24)
      hours = hours % 24
    }

    return { days, hours, minutes, seconds }
  }

  function parseISODuration(iso: string): Duration {
    const match = iso.match(
      /P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?/
    )

    if (!match) {
      return { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 }
    }

    const [, years, months, days, hours, minutes, seconds] = match

    return {
      years: Number(years || 0),
      months: Number(months || 0),
      days: Number(days || 0),
      hours: Number(hours || 0),
      minutes: Number(minutes || 0),
      seconds: Number(seconds || 0)
    }
  }

  function formatDurationTime(duration: Duration, shortLabel: boolean = true): string {
    const { hours = 0, minutes = 0, seconds = 0 } = duration

    const hoursLabel = shortLabel ? translatedHours.value.short : translatedHours.value.long
    const minutesLabel = shortLabel ? translatedMinutes.value.short : translatedMinutes.value.long
    const secondsLabel = shortLabel ? translatedSeconds.value.short : translatedSeconds.value.long

    if (hours <= 0 && minutes <= 0 && seconds <= 0) {
      return `0 ${secondsLabel}`
    }

    const paddedMinutes = minutes.toString().padStart(2, '0')
    const paddedSeconds = seconds.toString().padStart(2, '0')

    let baseTime = ''

    if (hours >= 1) {
      baseTime = `${hours} ${hoursLabel}`

      if (minutes >= 1) {
        baseTime += ` ${paddedMinutes} ${minutesLabel}`
      }
    } else if (minutes >= 1) {
      baseTime = `${minutes} ${minutesLabel}`

      if (seconds >= 1) {
        baseTime += ` ${paddedSeconds} ${secondsLabel}`
      }
    } else if (seconds >= 1) {
      baseTime = `${seconds} ${secondsLabel}`
    }

    return baseTime
  }

  function isoDurationToString(iso: string, shortLabels: boolean): string {
    const parsed = parseISODuration(iso)
    const normalized = normalizeDuration(parsed)
    return formatDurationTime(normalized, shortLabels)
  }

  return {
    formatDurationTime,
    isoDurationToString
  }
}

export default useTimeParser
